import Cookies from "js-cookie";
import {jwtDecode} from "jwt-decode";
import {IAppConfig} from "@commons/constants";
import {ApiResult, UserToken, WebshopRootScope} from "@/models";
import {INotificationService} from "@core/Notification";
import {QueryDic, toMB} from "@core/global";

export type RequestOption = RequestInit & {
    blockUI?: boolean;
    silent?: boolean;
    [key: string]: any;
}

export type DownloadOptions = {
    fileName?: string;
    type?: string;
}

export const writeMethods = [
    'POST',
    'PUT',
    'DELETE'
];

export type BaseApiFactory = {
    /**
     * @deprecated: use get() with data object
     * */
    get<T>(url: string, cancelToken?: AbortSignal, options?: RequestOption): Promise<ApiResult<T>>;

    getData<T>(url: string, data?: {
        [key: string]: string | boolean | number | undefined;
    } | QueryDic | object, cancelToken?: AbortSignal, options?: RequestOption): Promise<ApiResult<T>>;

    post<T>(url: string, data: {
        [key: string]: any;
    }, cancelToken?: AbortSignal, options?: RequestOption): Promise<ApiResult<T>>;

    postFormData<T>(url: string, file: File, data?: {
        [key: string]: any;
    }, cancelToken?: AbortSignal, options?: RequestOption): Promise<ApiResult<T>>;

    put<T>(url: string, data: {
        [key: string]: any;
    }, cancelToken?: AbortSignal, options?: RequestOption): Promise<ApiResult<T>>;

    delete<T>(url: string, data?: {
        [key: string]: any;
    }, cancelToken?: AbortSignal, options?: RequestOption): Promise<ApiResult<T>>;

    download(
        url: string,
        fileOptions?: DownloadOptions,
        requestOptions?: RequestOption,
        cancelToken?: AbortSignal
    ): Promise<void>;
}

export const registerBaseApiFactory = (app: IModule) => {
    app.factory('BaseApiFactory', [
        '$rootScope',
        '$q',
        'Notification',
        'APP_CONFIG',
        '$state',
        function (
            $rootScope: WebshopRootScope,
            $q,
            notification: INotificationService,
            APP_CONFIG: IAppConfig,
            $state
        ) {
            function isTokenExpired(token: string) {
                if (!token) return true;
                try {
                    const decoded = jwtDecode(token);
                    const currentTime = Date.now() / 1000;
                    return decoded.exp < (currentTime + 30); // Buffer 30 giây cho an toàn
                } catch (e) {
                    return true;
                }
            }

            function resolveBackendUrl(url: string) {
                if (url.indexOf('/v2') === 0) {
                    return APP_CONFIG.baseApiUrl + url;
                }

                if (url.indexOf('/api') === 0) {
                    return APP_CONFIG.baseApiUrl + url;
                }

                return url;
            }

            function request<T>(url: string, options: RequestOption = {}, cancelToken?: AbortSignal):
                Promise<ApiResult<T>> | Promise<Blob> {
                const deferred = $q.defer();
                options.signal = cancelToken;

                console.debug(`API Request: ${options.method || 'GET'} ${url}`);
                (async () => {
                    const isWriteRequest = writeMethods.includes(options.method) && options.silent !== true;

                    if (options.blockUI || isWriteRequest) {
                        $rootScope.apiState.blockApiCount = ($rootScope.apiState.blockApiCount || 0) + 1;
                        $rootScope.$applyAsync();
                    }

                    if (!options.silent) {
                        $rootScope.apiState.totalApiRunning = ($rootScope.apiState.totalApiRunning || 0) + 1;
                    }

                    // Nếu URL bắt đầu bằng /api, tự động chèn thêm Domain Backend
                    url = resolveBackendUrl(url);

                    let adminTokenRaw: string;
                    if (options.accessToken) {
                        options.headers = {
                            ...options.headers,
                            'Authorization': `Bearer ${options.accessToken}`
                        };
                    } else {
                        adminTokenRaw = Cookies.get('admin_token');
                    }

                    if (!adminTokenRaw) {
                        try {
                            const res = await fetch(url, options);
                            const result = await handleResponse<T>(res, options);
                            deferred.resolve(result);
                        } catch (e) {
                            finaliseRequest(options);
                            notification.showError(e.message || 'Bad request data.', false, true);
                            deferred.resolve({
                                IsSuccess: false,
                            } as ApiResult<T>);
                        }
                        return;
                    }

                    try {
                        const tokenData: UserToken = JSON.parse(adminTokenRaw) || {};
                        let accessToken = tokenData.AccessToken;

                        // try to get new token if it is expired from server before call api
                        if (accessToken && isTokenExpired(accessToken)) {
                            try {
                                const response = await fetch(resolveBackendUrl('/v2/api/auth/refresh-session'), {
                                    method: 'POST',
                                    headers: {
                                        'Authorization': `Bearer ${tokenData.RefreshToken}`
                                    }
                                });

                                const data: ApiResult<UserToken> = await response.json();

                                if (!data || !data.IsSuccess) {
                                    notification.showError('session_expired', false, true);
                                    Cookies.remove('admin_token');

                                    $state.go('account_login');
                                    deferred.resolve({
                                        IsSuccess: false,
                                    } as ApiResult<T>);
                                    return;
                                }

                                Cookies.set('admin_token', JSON.stringify(data.Value), {
                                    expires: 365,
                                    path: '/'
                                });

                                accessToken = data.Value.AccessToken;
                            } catch (e) {
                                console.log('Login token expired and failed to extend', JSON.stringify(e));
                                if (!e.message.includes('The operation was aborted')) {
                                    Cookies.remove('admin_token');
                                    notification.showError('session_expired', false, true);
                                }

                                $state.go('account_login');

                                deferred.resolve({
                                    IsSuccess: false,
                                } as ApiResult<T>);
                                return;
                            }
                        }

                        if (accessToken) {
                            options.headers = {
                                ...options.headers,
                                'Authorization': `Bearer ${accessToken}`
                            };

                            if (APP_CONFIG.currentEnvironment == 'Development') {
                                url += `${url.includes('?') ? '&' : '?'}token=${accessToken}`;
                            }
                        }

                    } catch (e) {
                        Cookies.remove('admin_token');
                        console.error("Lỗi parse token từ cookie", e);
                    }

                    try {
                        const res = await fetch(url, options);

                        const result = options.responseType === 'blob'
                            ? await handleResponseFile(res, options)
                            : await handleResponse<T>(res, options);
                        deferred.resolve(result);
                    } catch (e) {
                        finaliseRequest(options);
                        if (!e.name?.includes('AbortError')) {
                            notification.showError(e.message || 'Bad request data.', false, true);
                        }
                        deferred.resolve({
                            IsSuccess: false
                        } as ApiResult<T>);
                    }
                })();

                return deferred.promise;
            }

            function requestWithProgress<T>(url: string, options: any = {}, cancelToken?: AbortSignal): Promise<ApiResult<T>> {
                const deferred = $q.defer();
                options.signal = cancelToken;

                console.debug(`API Progress Request: ${options.method || 'POST'} ${url}`);
                (async () => {
                    const isWriteRequest = writeMethods.includes(options.method) && options.silent !== true;

                    if (options.blockUI || isWriteRequest) {
                        $rootScope.apiState.blockApiCount = ($rootScope.apiState.blockApiCount || 0) + 1;
                    }

                    if (!options.silent) {
                        $rootScope.apiState.totalApiRunning = ($rootScope.apiState.totalApiRunning || 0) + 1;
                    }

                    url = resolveBackendUrl(url);

                    let adminTokenRaw = Cookies.get('admin_token');
                    let accessToken = options.accessToken || '';

                    if (!options.accessToken && adminTokenRaw) {
                        try {
                            const tokenData: UserToken = JSON.parse(adminTokenRaw) || {};
                            accessToken = tokenData.AccessToken;

                            if (accessToken && isTokenExpired(accessToken)) {
                                try {
                                    const response = await fetch(resolveBackendUrl('/v2/api/auth/refresh-session'), {
                                        method: 'POST',
                                        headers: {'Authorization': `Bearer ${tokenData.RefreshToken}`}
                                    });
                                    const data: ApiResult<UserToken> = await response.json();
                                    if (!data || !data.IsSuccess) {
                                        notification.showError('session_expired', false, true);
                                        Cookies.remove('admin_token');
                                        $state.go('account_login');
                                        deferred.resolve({IsSuccess: false} as ApiResult<T>);
                                        return;
                                    }
                                    Cookies.set('admin_token', JSON.stringify(data.Value), {expires: 365, path: '/'});
                                    accessToken = data.Value.AccessToken;
                                } catch (e) {
                                    console.log('Login token expired and failed to extend', JSON.stringify(e));
                                    if (!e.message.includes('The operation was aborted')) {
                                        Cookies.remove('admin_token');
                                        notification.showError('session_expired', false, true);
                                    }
                                    $state.go('account_login');
                                    deferred.resolve({IsSuccess: false} as ApiResult<T>);
                                    return;
                                }
                            }
                        } catch (e) {
                            Cookies.remove('admin_token');
                            console.error("Lỗi parse token từ cookie", e);
                        }
                    }

                    try {
                        const xhr = new XMLHttpRequest();
                        xhr.open(options.method || 'POST', url, true);

                        if (accessToken) {
                            xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
                        }
                        if (options.headers) {
                            Object.keys(options.headers).forEach(key => {
                                if (key.toLowerCase() !== 'authorization') {
                                    xhr.setRequestHeader(key, options.headers[key]);
                                }
                            });
                        }

                        if (options?.uploadEventHandlers) {
                            xhr.upload.onprogress = (e) => {
                                if (!options.uploadEventHandlers.progress) {
                                    return;
                                }
                                let currentProgress = Math.floor((e.loaded / e.total) * 100);
                                currentProgress = currentProgress >= 100 ? 99 : currentProgress;

                                const data = {
                                    percent: currentProgress,
                                    total: toMB(e.total),
                                    current: toMB(e.loaded)
                                };
                                options.uploadEventHandlers.progress(data);
                            };


                            xhr.upload.onerror = (error) => {
                                if (options.uploadEventHandlers?.error)
                                    options.uploadEventHandlers.error({code: error.type || 'Unknown Error'});
                                finaliseRequest(options);
                            };
                        }

                        if (cancelToken) {
                            cancelToken.addEventListener('abort', () => {
                                xhr.abort();
                                finaliseRequest(options);
                                deferred.resolve({IsSuccess: false} as ApiResult<T>);
                            });
                        }

                        xhr.onload = async () => {
                            finaliseRequest(options);
                            if (xhr.status >= 200 && xhr.status < 300) {
                                try {
                                    const result = JSON.parse(xhr.responseText);
                                    if (options.uploadEventHandlers?.success)
                                        options.uploadEventHandlers.success(result);
                                    deferred.resolve(result);
                                } catch (e) {
                                    const error = {IsSuccess: true, Value: xhr.responseText} as any;
                                    if (options.uploadEventHandlers?.error)
                                        options.uploadEventHandlers.error(error);
                                    deferred.resolve(error);
                                }
                            } else {
                                const result = {
                                    IsSuccess: false,
                                    Message: xhr.statusText || 'Bad request data.'
                                } as ApiResult<T>;
                                notification.showError(xhr.statusText || 'Bad request data.', false, true);
                                if (options.uploadEventHandlers?.error)
                                    options.uploadEventHandlers.error(result);
                                deferred.resolve(result);
                            }
                        };

                        xhr.onerror = () => {
                            const result = {IsSuccess: false, Message: 'Network Error'} as ApiResult<T>;
                            finaliseRequest(options);
                            notification.showError('Network_Error', false, true);
                            if (options.uploadEventHandlers.error)
                                options.uploadEventHandlers.error(result);
                            deferred.resolve(result);
                        };

                        xhr.send(options.body);

                    } catch (e) {
                        finaliseRequest(options);
                        notification.showError(e.message || 'Bad request data.', false, true);
                        deferred.resolve({IsSuccess: false} as ApiResult<T>);
                    }
                })();

                return deferred.promise as Promise<ApiResult<T>>;
            }


            async function handleResponse<T>(response: Response, options: {
                [key: string]: any;
            }) {
                finaliseRequest(options);

                if (response.ok) {
                    return await response.json() as ApiResult<T>;
                }

                let apiError;
                try {
                    apiError = await response.json() || {message: "unknown_error"};
                } catch (e) {
                    switch (response.status) {
                        case 401:
                            apiError = {
                                message: 'session_expired'
                            }
                            break;
                        case 403:
                            apiError = {
                                message: 'permission_denied'
                            }
                            break;
                        case  404: {
                            apiError = {
                                message: 'Request api url not found, see console for more details.'

                            }
                            break;
                        }
                        case 501:
                        case 502:
                        case 503:
                            apiError = {
                                message: 'Failed to connect to server'
                            }
                            break;
                        default:
                            apiError = {
                                message: "unknown_error"
                            }
                            break;
                    }
                }

                const {errors, message, Message, Errors, Description} = apiError;
                let errorMessage = message || Message || Description;
                const errorsData = errors || Errors;

                if (errorsData) {
                    errorMessage = Object.keys(errorsData).map(x => x + ': ' + errorsData[x]).join('\n');
                }
                if (apiError?.Error?.Code === 'NoContent' && options.silent) {
                    return apiError as ApiResult<T>;
                }
                
                notification.showError(errorMessage || 'Bad request data.', false, true);
                return apiError as ApiResult<T>;
            }

            function handleResponseFile(response: Response, options: RequestOption) {
                finaliseRequest(options);
                return response.blob();
            }

            function get<T>(url: string, cancelToken?: AbortSignal, options?: RequestOption): Promise<ApiResult<T>> {
                const requestOptions = {
                    method: 'GET',
                    ...options
                };
                return request(url, requestOptions, cancelToken) as Promise<ApiResult<T>>
            }

            function getData<T>(url: string,
                                data: {
                                    [key: string]: string | boolean | number | undefined;
                                } | QueryDic,
                                cancelToken?: AbortSignal,
                                options: RequestOption = {}
            ): Promise<ApiResult<T>> {
                try {
                    let queryString = ''

                    if (data) {
                        const params = new URLSearchParams();
                        Object.keys(data).forEach(key => {
                            if (data[key] === undefined ||
                                data[key] === '' ||
                                data[key] === null) {
                                delete data[key];
                                return
                            }

                            params.append(key, data[key]);
                        });

                        queryString = params.toString();
                    }

                    const urlWithQuery = queryString ? `${url}${url.includes('?') ? '&' : '?'}${queryString}` : url;

                    const requestOptions = {
                        method: 'GET',
                        ...options
                    };
                    return request(urlWithQuery, requestOptions, cancelToken) as Promise<ApiResult<T>>;
                } catch (e) {
                    console.error('Error in getData:', e);
                    return Promise.resolve({
                        IsSuccess: false,
                    } as ApiResult<T>);
                }
            }

            function post<T>(url: string, data: {
                [key: string]: any;
            }, cancelToken?: AbortSignal, options?: RequestOption): Promise<ApiResult<T>> {
                const requestOptions = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data),
                    ...options
                };
                return request(url, requestOptions, cancelToken) as Promise<ApiResult<T>>;
            }

            function postFormData<T>(url: string, file: File, data: {
                [key: string]: any;
            }, cancelToken?: AbortSignal, options?: RequestOption): Promise<ApiResult<T>> {
                const formData = new FormData();
                formData.append('file', file);
                if (data) {
                    for (const key in data) {
                        formData.append(key, data[key]);
                    }
                }
                const requestOptions = {
                    method: 'POST',
                    body: formData,
                    ...options
                };
                return requestWithProgress(url, requestOptions, cancelToken) as Promise<ApiResult<T>>;
            }

            function put<T>(url: string, data: {
                [key: string]: any;
            }, cancelToken?: AbortSignal, options?: RequestOption): Promise<ApiResult<T>> {
                const requestOptions = {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: !!data ? JSON.stringify(data) : undefined,
                    ...options
                };
                return request(url, requestOptions, cancelToken) as Promise<ApiResult<T>>;
            }

            function deleteRequest<T>(url: string, data?: {
                [key: string]: any;
            }, cancelToken: AbortSignal = null, options: RequestOption = {
                headers: {}
            }): Promise<ApiResult<T>> {
                const requestOptions = {
                    method: 'DELETE',
                    body: !!data ? JSON.stringify(data) : undefined,
                    ...options
                };

                if (!!data) {
                    requestOptions.headers["Content-Type"] = 'application/json-patch+json';
                }

                return request(url, requestOptions, cancelToken) as Promise<ApiResult<T>>;
            }

            function download(
                url: string,
                fileOptions: DownloadOptions = {fileName: "download.pdf", type: 'application/pdf'},
                requestOptions: RequestOption = {}, cancelToken?: AbortSignal
            ) {
                requestOptions = {
                    method: 'GET',
                    responseType: 'blob',
                    ...requestOptions,
                }
                console.log("Download requestOptions", requestOptions);
                requestOptions.headers = requestOptions.headers || {};
                requestOptions.headers['Accept'] = requestOptions.headers['Accept'] || fileOptions.type || 'application/pdf';

                return request(url, requestOptions, cancelToken).then((blob) => {
                    const fileURL = window.URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = fileURL;
                    link.download = fileOptions.fileName || "download.pdf";
                    link.click();

                    // Xóa bộ nhớ tạm sau khi click
                    setTimeout(() => {
                        window.URL.revokeObjectURL(fileURL);
                    }, 100);
                });
            }

            function finaliseRequest(config: any) {
                const isWriteRequest = writeMethods.includes(config.method) && config.silent !== true;
                if (config?.blockUI || isWriteRequest) {
                    $rootScope.apiState.blockApiCount = Math.max(0, ($rootScope.apiState.blockApiCount || 1) - 1);
                    $rootScope.$applyAsync();
                }

                // 2. Xử lý totalApiRunning
                if (!config?.silent) {
                    $rootScope.apiState.totalApiRunning = $rootScope.apiState.totalApiRunning > 1 ? $rootScope.apiState.totalApiRunning - 1 : 0;
                }
            }

            return {
                get,
                getData,
                post,
                postFormData,
                put,
                delete: deleteRequest,
                download
            } as BaseApiFactory;
        }
    ]);
};
